import React, { useState, useMemo, useEffect } from "react";
import { useData } from "../context/DataContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Search, Building2, User, UserPlus, CheckCircle, Eye } from "lucide-react";
import { Policy, AssignmentDetails, Memo, AssignmentUser, User as UserType } from "../types";
import { toast } from "sonner";
import { deleteFromMainObj } from "../utils/helpers";

interface AddUsersToPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  policy?: Policy,
  memo?: Memo,
  assignment: AssignmentDetails;
  isRemove?: boolean;
  documentType: string;
  onSuccess?: () => void;
}

export default function AddUsersToPolicyModal({ isOpen, onClose, policy, memo, assignment, isRemove, documentType, onSuccess }: AddUsersToPolicyModalProps) {
  const {
    users,
    departments,
    policyAssignments,
    addUsersToPolicy,
    removeUsersFromPolicy,
    getDepartmentByCode,
    addUsersToMemo,
    removeUsersFromMemo
  } = useData();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {

  }, [documentType]);

  // Get users already assigned to this policy - simplified using individual assignment records
  const assignedUserIds = useMemo(() => {
    return assignment.users.map(user => user.id);
  }, [policyAssignments]);

  // useEffect(() => {
  //   setSelectedUserIds(assignedUserIds);
  // }, [])

  // Get available users (not already assigned)
  const availableUsers = useMemo(() => {
    return users.filter(user =>
      user.isActive &&
      !user.archived &&
      !assignedUserIds.includes(user.id)
    );
  }, [users, assignedUserIds]);

  // Filter users based on search and department
  const filteredUsers = useMemo(() => {
    const filtered = isRemove ? assignment.users : availableUsers.filter(user => {
      const matchesSearch =
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesDepartment =
        departmentFilter === "all" ||
        (typeof user.department !== 'string' && user.department.departmentCode) == departmentFilter;

      return matchesSearch && matchesDepartment;
    });

    if (isRemove) {
      return filtered as typeof assignment.users;
    }

    return filtered;
  }, [availableUsers, searchTerm, departmentFilter]);

  const activeUsers = isRemove
    ? (filteredUsers as typeof assignment.users).filter(user => !(('signedDate' in user && typeof user.signedDate === 'string') || ('readDate' in user && typeof user.readDate === 'string')))
    : filteredUsers;

  const usersToConfirm = useMemo(() => {
    return assignment.users.filter(u => selectedUserIds.includes(u.id));
  }, [selectedUserIds, filteredUsers]);

  const handleUserToggle = (userId: string, isCompleted?: boolean) => {
    if (isRemove && isCompleted) {
      return;
    }

    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === activeUsers.length && activeUsers.length > 0) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(activeUsers.map((user: AssignmentUser | UserType) => user.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedUserIds.length === 0) {
      toast.error("Please select at least one user");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isRemove) {
        setShowConfirmation(true);
        return;
      }

      if (policy) {
        await addUsersToPolicy(policy?.id || "", selectedUserIds);
      } else if (memo) {
        await addUsersToMemo(memo?.id || "", selectedUserIds);
      } else {
        throw ('Error adding user(s) assignment.')
      }

      onClose();
      if (onSuccess) onSuccess();
      setSelectedUserIds([]);
      setSearchTerm("");
      setDepartmentFilter("all");
    } catch (error) {
      console.error(`Error adding users to ${documentType}:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRemove = async () => {
    setIsSubmitting(true);
    try {
      if (policy) {
        await removeUsersFromPolicy(policy?.id || "", selectedUserIds);
      } else if (memo) {
        await removeUsersFromMemo(memo?.id || "", selectedUserIds);
      } else {
        throw ('Missing document error.')
      }

      // Close both dialogs on success
      setShowConfirmation(false);
      onClose();
      if (onSuccess) onSuccess();

      // Reset state
      setSelectedUserIds([]);
      setSearchTerm("");
      setDepartmentFilter("all");
    } catch (error) {
      // console.error(`Error removing users from ${documentType}:`, error);
      // Optionally, you could close the confirmation dialog to show the main one again
      // setShowConfirmation(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setSelectedUserIds([]);
      setSearchTerm("");
      setDepartmentFilter("all");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {isRemove ? 'Remove' : 'Add'} Users to {documentType}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1 pt-2"> {/* Added pt-2 for spacing */}
              <span>{isRemove ? 'Remove users from' : 'Add users to'}:</span>
              <div className="font-medium text-foreground break-words">"{policy?.title || memo?.subject || "Unkown document"}"</div>
              <span className="text-sm">{isRemove ? 'Only assigned users are shown.' : 'Only active users not already assigned are shown.'}</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name or email..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-60">
                <SelectValue placeholder="Filter by department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments
                  .filter(dept => !dept.archived)
                  .map((dept) => (
                    <SelectItem key={dept.id} value={dept.departmentCode}>
                      {dept.departmentCode} - {dept.departmentName}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
          </div>

          {/* Selection Summary */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedUserIds.length === activeUsers.length && activeUsers.length > 0}
                onCheckedChange={handleSelectAll}
                disabled={filteredUsers.length === 0}
              />
              <span className="text-sm text-muted-foreground">
                Select all
                {/* ({filteredUsers.length} users) */}
              </span>
            </div>

            {selectedUserIds.length > 0 && (
              <Badge variant="secondary">
                {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
              </Badge>
            )}
          </div>

          {/* Users List */}
          <div className="flex-1 overflow-y-auto border rounded-md">
            {filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <User className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No users available</h3>
                <p className="text-sm text-muted-foreground">
                  {availableUsers.length === 0
                    ? "All active users are already assigned to this policy"
                    : "No users match your current filters"
                  }
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredUsers.map((user, index) => {
                  const department = user.department;
                  const isSelected = selectedUserIds.includes(user.id);
                  const isCompleted = isRemove && (('signedDate' in user && typeof user.signedDate === 'string') || ('readDate' in user && typeof user.readDate === 'string'));

                  return (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-3 rounded-md cursor-pointer transition-colors ${isCompleted
                        ? "bg-gray-100 opacity-50 cursor-not-allowed" // Style for disabled
                        : isSelected
                          ? "bg-blue-50 border border-blue-200"
                          : "hover:bg-gray-50 cursor-pointer"
                        }`}
                      onClick={() => handleUserToggle(user.id, isCompleted)}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isCompleted}
                        onChange={() => handleUserToggle(user.id, isCompleted)}
                      />

                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{user.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                      </div>

                      {isCompleted ? (
                        <>
                          <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                            {policy?.requireSignature || memo?.requireSignature ? <CheckCircle className="h-3 w-3 mr-1.5" /> : <Eye className="h-3 w-3 mr-1.5" />}
                            Completed
                          </Badge>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Building2 className="h-4 w-4" />
                            <span className="truncate">
                              {typeof department === 'object' && department ? `${department.departmentCode} - ${department.departmentName}` : "Unknown"}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Building2 className="h-4 w-4" />
                          <span className="truncate">
                            {typeof department === 'object' && department ? `${department.departmentCode} - ${department.departmentName}` : "Unknown"}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedUserIds.length === 0 || isSubmitting}
            className="flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                Adding...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" />
                {isRemove ? 'Remove' : 'Add'} {selectedUserIds.length} User{selectedUserIds.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-destructive" />
              Confirm User Removal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You are about to remove the following {usersToConfirm.length} user(s) from this policy assignment:
            </p>

            <div className="max-h-28 overflow-y-auto rounded-md border bg-muted/50 p-3 text-sm">
              <ul className="space-y-1">
                {usersToConfirm.map(user => (
                  <li key={user.id} className="font-medium">{user.name}</li>
                ))}
              </ul>
            </div>

            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
              <h4 className="font-medium text-destructive mb-2">This action will:</h4>
              <ul className="text-sm text-destructive/80 space-y-1">
                <li>• Remove the policy from their "My Policies" page.</li>
                <li>• Mark their assignments as deleted.</li>
              </ul>
            </div>

            <DialogFooter className="flex justify-between gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowConfirmation(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmRemove}
                disabled={isSubmitting}
                className="flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                    Removing...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4" />
                    Confirm & Remove
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
} 